import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Linkedin, Check, Loader2, Sparkles, ChevronDown } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const isValidUrl = (str) => {
  if (!str) return true;
  try { new URL(str); return true; } catch { return false; }
};

const TONES = [
  { id: "corporate", label: "Professional", icon: "💼" },
  { id: "friendly", label: "Friendly", icon: "😊" },
  { id: "minimal", label: "Concise", icon: "✂️" },
];

export const RecommendationForm = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({
    name: "",
    role_company: "",
    linkedin_url: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");

  // AI Rewrite state
  const [showAiHelper, setShowAiHelper] = useState(false);
  const [selectedTone, setSelectedTone] = useState("corporate");
  const [isRewriting, setIsRewriting] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const typingRef = useRef(null);

  useEffect(() => {
    return () => { if (typingRef.current) clearInterval(typingRef.current); };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    if (name === "message" && isTyping) {
      clearInterval(typingRef.current);
      setIsTyping(false);
    }
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Please enter your name";
    if (!form.role_company.trim()) errs.role_company = "Please enter your position";
    if (form.linkedin_url.trim() && !isValidUrl(form.linkedin_url.trim())) {
      errs.linkedin_url = "Please enter a valid URL";
    }
    if (!form.message.trim()) {
      errs.message = "Please share your thoughts";
    } else if (form.message.trim().length < 10) {
      errs.message = "Please write at least a few words";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleRewritePro = async () => {
    if (!form.message.trim()) {
      setErrors((prev) => ({ ...prev, message: "Write something first to enhance" }));
      return;
    }

    setIsRewriting(true);
    setServerError("");
    if (typingRef.current) clearInterval(typingRef.current);

    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/rewrite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: form.message, tone: selectedTone }),
      });
      if (!res.ok) throw new Error("Rewrite failed");
      const data = await res.json();
      const fullText = data.rewritten_text;

      // Typewriter effect
      let i = 0;
      setIsTyping(true);
      setForm((prev) => ({ ...prev, message: "" }));
      typingRef.current = setInterval(() => {
        i++;
        const partial = fullText.slice(0, i);
        setForm((prev) => ({ ...prev, message: partial }));
        if (i >= fullText.length) {
          clearInterval(typingRef.current);
          setIsTyping(false);
        }
      }, 12);
    } catch {
      setServerError("AI service unavailable. You can still submit your message as-is.");
    } finally {
      setIsRewriting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    if (!validate()) return;

    setStatus("submitting");
    try {
      const payload = {
        ...form,
        linkedin_url: form.linkedin_url.trim() || "",
      };
      const res = await fetch(`${BACKEND_URL}/api/recommendations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Submission failed");
      setStatus("success");
      setForm({ name: "", role_company: "", linkedin_url: "", message: "" });
      setErrors({});
    } catch {
      setStatus("idle");
      setServerError("Something went wrong. Please try again.");
    }
  };

  const handleClose = () => {
    if (status !== "submitting") {
      setStatus("idle");
      setErrors({});
      setServerError("");
      if (typingRef.current) clearInterval(typingRef.current);
      setIsTyping(false);
      setShowAiHelper(false);
      setSelectedTone("corporate");
      onClose();
    }
  };

  const inputCls = (field) =>
    `w-full bg-zinc-900/50 border text-white placeholder-zinc-600 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-white/10 focus:border-zinc-600 transition-all duration-200 ${errors[field] ? "border-red-500/50" : "border-zinc-800/80"}`;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          data-testid="recommendation-modal-overlay"
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 16 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            onClick={(e) => e.stopPropagation()}
            data-testid="recommendation-modal"
            className="relative w-full max-w-md bg-[#0C0C0D] border border-zinc-800/60 rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-800/40">
              <div>
                <h3 className="text-lg font-semibold text-white">Share Your Thoughts</h3>
                <p className="text-xs text-zinc-500 mt-0.5">Your feedback helps me grow</p>
              </div>
              <button
                data-testid="recommendation-close-btn"
                onClick={handleClose}
                className="p-2 rounded-lg text-zinc-500 hover:text-white hover:bg-white/5 transition-colors duration-200"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            {status === "success" ? (
              <div data-testid="recommendation-success" className="px-6 py-14 text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-5">
                  <Check size={24} className="text-emerald-400" strokeWidth={2} />
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Thank you!</h4>
                <p className="text-sm text-zinc-500 max-w-xs mx-auto leading-relaxed">
                  Your message has been received and will be reviewed shortly.
                </p>
                <button
                  data-testid="recommendation-success-close"
                  onClick={handleClose}
                  className="mt-8 inline-flex items-center px-6 py-2.5 bg-white text-black hover:bg-zinc-200 rounded-full text-sm font-medium transition-colors duration-200"
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="px-6 py-5 space-y-4">
                {/* Name & Role in one row on larger screens */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="rec-name" className="block text-sm text-zinc-400 mb-1.5">
                      Name
                    </label>
                    <input
                      id="rec-name"
                      name="name"
                      type="text"
                      value={form.name}
                      onChange={handleChange}
                      data-testid="recommendation-name-input"
                      placeholder="Your name"
                      className={inputCls("name")}
                    />
                    {errors.name && (
                      <p data-testid="error-name" className="text-xs text-red-400 mt-1">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="rec-role" className="block text-sm text-zinc-400 mb-1.5">
                      Position
                    </label>
                    <input
                      id="rec-role"
                      name="role_company"
                      type="text"
                      value={form.role_company}
                      onChange={handleChange}
                      data-testid="recommendation-role-input"
                      placeholder="Role & Company"
                      className={inputCls("role_company")}
                    />
                    {errors.role_company && (
                      <p data-testid="error-role" className="text-xs text-red-400 mt-1">{errors.role_company}</p>
                    )}
                  </div>
                </div>

                {/* LinkedIn (Optional) */}
                <div>
                  <label htmlFor="rec-linkedin" className="block text-sm text-zinc-400 mb-1.5">
                    LinkedIn <span className="text-zinc-600">(optional)</span>
                  </label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-600">
                      <Linkedin size={14} strokeWidth={1.5} />
                    </div>
                    <input
                      id="rec-linkedin"
                      name="linkedin_url"
                      type="url"
                      value={form.linkedin_url}
                      onChange={handleChange}
                      data-testid="recommendation-linkedin-input"
                      placeholder="linkedin.com/in/your-profile"
                      className={`${inputCls("linkedin_url")} pl-10`}
                    />
                  </div>
                  {errors.linkedin_url && (
                    <p data-testid="error-linkedin" className="text-xs text-red-400 mt-1">{errors.linkedin_url}</p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="rec-message" className="block text-sm text-zinc-400 mb-1.5">
                    Your Message
                  </label>
                  <textarea
                    id="rec-message"
                    name="message"
                    rows={4}
                    value={form.message}
                    onChange={handleChange}
                    data-testid="recommendation-message-input"
                    placeholder="Share your experience, feedback, or thoughts..."
                    className={`${inputCls("message")} resize-none`}
                  />
                  {errors.message && (
                    <p data-testid="error-message" className="text-xs text-red-400 mt-1">{errors.message}</p>
                  )}
                </div>

                {/* AI Helper - Collapsible */}
                <div className="border border-zinc-800/60 rounded-xl overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setShowAiHelper(!showAiHelper)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-800/20 transition-colors duration-200"
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles size={14} className="text-amber-400/80" />
                      <span className="text-sm text-zinc-400">Need help writing?</span>
                    </div>
                    <ChevronDown 
                      size={16} 
                      className={`text-zinc-600 transition-transform duration-200 ${showAiHelper ? 'rotate-180' : ''}`} 
                    />
                  </button>
                  
                  <AnimatePresence>
                    {showAiHelper && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 pt-1 space-y-3 border-t border-zinc-800/40">
                          <p className="text-xs text-zinc-500">
                            AI can help polish your message. Choose a tone:
                          </p>
                          
                          {/* Tone Selection */}
                          <div className="flex gap-2">
                            {TONES.map((tone) => (
                              <button
                                key={tone.id}
                                type="button"
                                onClick={() => setSelectedTone(tone.id)}
                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-all duration-200 ${
                                  selectedTone === tone.id
                                    ? "bg-amber-500/10 border-amber-500/30 text-amber-400"
                                    : "bg-zinc-900/50 border-zinc-800 text-zinc-500 hover:border-zinc-700"
                                }`}
                              >
                                <span>{tone.icon}</span>
                                {tone.label}
                              </button>
                            ))}
                          </div>

                          {/* Enhance Button */}
                          <button
                            type="button"
                            onClick={handleRewritePro}
                            disabled={isRewriting || isTyping || !form.message.trim()}
                            className="w-full inline-flex items-center justify-center gap-2 bg-amber-500/90 hover:bg-amber-500 text-black disabled:opacity-40 disabled:cursor-not-allowed px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-200"
                          >
                            {isRewriting || isTyping ? (
                              <>
                                <Loader2 size={14} className="animate-spin" />
                                {isTyping ? "Writing..." : "Enhancing..."}
                              </>
                            ) : (
                              <>
                                <Sparkles size={14} />
                                Enhance Message
                              </>
                            )}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Server error */}
                {serverError && (
                  <p data-testid="recommendation-error" className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                    {serverError}
                  </p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === "submitting" || isRewriting || isTyping}
                  data-testid="recommendation-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 bg-white text-black hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3 rounded-full font-medium text-sm transition-colors duration-200"
                >
                  {status === "submitting" ? (
                    <>
                      <Loader2 size={16} className="animate-spin" strokeWidth={1.5} />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={14} strokeWidth={1.5} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
